import { useEffect, useState, useMemo } from 'react';
import { CheckCircle2, XCircle, Loader2 } from 'lucide-react';
import {
  fetchLatestImportLog,
  fetchLatestCompletedImportLog,
  type ImportLog,
} from '@/api/mapImport';
import { formatRelativeTime } from '@/utils/date';

function formatTime(h: number, m: number): string {
  const period = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 || 12;
  return m === 0 ? `${h12} ${period}` : `${h12}:${String(m).padStart(2, '0')} ${period}`;
}

function convertUkTime(ukHour: number, ukMinute: number): string {
  try {
    const now = new Date();
    const ukDate = new Date(now.toLocaleString('en-US', { timeZone: 'Europe/London' }));
    const localDate = new Date(now);
    const offsetMs = localDate.getTime() - ukDate.getTime();

    const base = new Date();
    base.setHours(ukHour, ukMinute, 0, 0);
    const local = new Date(base.getTime() + offsetMs);

    return formatTime(local.getHours(), local.getMinutes());
  } catch {
    return `${formatTime(ukHour, ukMinute)} GMT`;
  }
}

export function MapSyncFooter() {
  const [latest, setLatest] = useState<ImportLog | null>(null);
  const [lastCompleted, setLastCompleted] = useState<ImportLog | null>(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const [log, completed] = await Promise.all([
          fetchLatestImportLog(),
          fetchLatestCompletedImportLog(),
        ]);
        if (!cancelled) {
          setLatest(log);
          setLastCompleted(completed);
        }
      } catch {
        // Silently fail — footer just shows static text
      } finally {
        if (!cancelled) setLoaded(true);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const local1030AM = useMemo(() => convertUkTime(10, 30), []);
  const local2PM = useMemo(() => convertUkTime(14, 0), []);

  const scheduleText = `Changes before ${local1030AM} appear by ${local2PM} the same day. Changes after ${local1030AM} appear the next day.`;

  if (!loaded) return null;

  const isProcessing = latest?.status === 'processing' || latest?.status === 'pending';
  const isFailed = latest?.status === 'failed';
  const isCompleted = latest?.status === 'completed';

  let icon = null;
  let statusText = '';

  if (isProcessing) {
    icon = <Loader2 className="w-3.5 h-3.5 text-slate-400 animate-spin flex-shrink-0" />;
    statusText = 'MAP sync in progress — data will update shortly. ';
  } else if (isFailed) {
    icon = <XCircle className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />;
    const lastGoodTime = lastCompleted?.import_completed_at ?? lastCompleted?.created_at;
    statusText = `Last MAP sync failed${lastGoodTime ? ` — data last updated ${formatRelativeTime(lastGoodTime)}` : ''}. `;
  } else if (isCompleted && latest) {
    icon = <CheckCircle2 className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />;
    const completedTime = latest.import_completed_at ?? latest.created_at;
    statusText = `Data last synced from MAP ${formatRelativeTime(completedTime)}. `;
  }

  return (
    <div className="flex items-center justify-center gap-2 px-6 py-1">
      {icon}
      <p className="text-xs text-slate-400 italic">
        {statusText}
        {scheduleText}
      </p>
    </div>
  );
}
